import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { BookOpen, Clock, User, ChevronRight, CreditCard, Plus, Award, Lock } from 'lucide-react'
import CoursePickerModal from '../../components/student/CoursePickerModal'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function MyCourses() {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEnrollModal, setShowEnrollModal] = useState(false)

  useEffect(() => {
    fetchCourses()
  }, [])

  const fetchCourses = async () => {
    try {
      const { data } = await api.get('/students/courses')
      setCourses(data)
    } catch (error) {
      toast.error('Failed to load courses')
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <LoadingSpinner label="Loading courses" />

  return (
    <div className="space-y-10">
      {showEnrollModal && (
        <CoursePickerModal
          title="Enroll in More Courses"
          subtitle="Select one or more courses. You can submit multiple enrollment requests at once."
          onClose={() => setShowEnrollModal(false)}
          onEnrolled={fetchCourses}
        />
      )}

      {/* Header */}
      <div className="cinema-page-header flex justify-between items-center">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light">My Courses</h1>
        </div>
        <button onClick={() => setShowEnrollModal(true)} className="cinema-btn-primary text-xs sm:text-sm px-5 py-2.5 min-h-0">
          <Plus size={15} /> Enroll in More
        </button>
      </div>

      {courses.length === 0 ? (
        <div className="cinema-empty">
          <BookOpen size={36} className="mx-auto mb-4 text-cinema-muted/20" />
          <p className="font-sans text-base text-cinema-muted mb-5">No courses enrolled yet.</p>
          <button onClick={() => setShowEnrollModal(true)} className="cinema-btn-primary text-xs sm:text-sm px-6 py-2.5 min-h-0">
            <Plus size={15} /> Browse and Enroll
          </button>
        </div>
      ) : (
        <div className="cinema-list">
          {courses.map((course) => {
            const isPending = course.enrollment_status === 'pending_payment'
            const pct = course.completion_percentage || 0
            return (
              <Link
                key={course.id}
                to={`/student/courses/${course.id}`}
                className="cinema-list-row group"
              >
                {/* Thumbnail */}
                {course.thumbnail ? (
                  <img src={course.thumbnail} alt={course.title} className="w-14 h-14 object-cover rounded-sm flex-shrink-0" loading="lazy" decoding="async" />
                ) : (
                  <div className="w-14 h-14 bg-white/3 flex items-center justify-center rounded-sm flex-shrink-0">
                    <BookOpen size={20} className="text-cinema-muted/30" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{course.title}</p>
                    {course.study_mode && (
                      <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${
                        course.study_mode === 'ONLINE'
                          ? 'bg-purple-500/10 text-purple-400 border-purple-500/25'
                          : 'bg-blue-500/10 text-blue-400 border-blue-500/25'
                      }`}>
                        {course.study_mode === 'ONLINE' ? 'Online' : 'Physical / On Campus'}
                      </span>
                    )}
                    {isPending && (
                      <span className="px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide bg-yellow-500/10 text-yellow-400 border border-yellow-500/25 rounded-sm">
                        Pending
                      </span>
                    )}
                    {!isPending && course.enrollment_status === 'active' && course.payment_progress?.amount > 0 && (
                      <span className={`px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${
                        course.payment_progress.unlock_percent === 0
                          ? 'bg-amber-500/10 text-amber-400 border-amber-500/25'
                          : course.payment_progress.unlock_percent < 100
                          ? 'bg-blue-500/10 text-blue-400 border-blue-500/25'
                          : 'bg-green-500/10 text-green-400 border-green-500/25'
                      }`}>
                        {course.payment_progress.unlock_percent === 0
                          ? 'Deposit Required'
                          : course.payment_progress.unlock_percent < 100
                          ? `${course.payment_progress.percent_paid}% Paid`
                          : 'Fully Paid'
                        }
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-2.5 text-xs sm:text-sm text-cinema-muted font-medium">
                    <span className="font-sans">{course.course_code}</span>
                    {course.trainer_first_name && (
                      <span className="flex items-center gap-1 font-sans">
                        <User size={12} /> {course.trainer_first_name} {course.trainer_last_name}
                      </span>
                    )}
                    <span className="flex items-center gap-1 font-sans">
                      <Clock size={12} /> {course.duration_weeks ? `${course.duration_weeks}w` : course.duration || 'N/A'}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-white/5 max-w-xs">
                    <div className="bg-cinema-glow h-1.5 transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="font-sans text-xs text-cinema-muted mt-1 font-medium">{pct}% complete</p>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {course.fee && (
                    <span className="font-display text-xs sm:text-sm text-cinema-glow font-medium">
                      {Number(course.fee).toLocaleString()} UGX
                    </span>
                  )}
                  {course.grade_percentage != null && (
                    <span className="flex items-center gap-1 font-sans text-xs sm:text-sm text-cinema-muted font-medium">
                      <Award size={13} /> {course.grade_percentage}%
                    </span>
                  )}
                  <ChevronRight size={16} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
